/**
Copyright (C) 2018 Andras Sarvari <andras.sarvari@sarvaria.net>

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted, provided that the above
copyright notice and this permission notice appear in all copies.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES
WITH REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF
MERCHANTABILITY AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR
ANY SPECIAL, DIRECT, INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES
WHATSOEVER RESULTING FROM LOSS OF USE, DATA OR PROFITS, WHETHER IN AN
ACTION OF CONTRACT, NEGLIGENCE OR OTHER TORTIOUS ACTION, ARISING OUT OF
OR IN CONNECTION WITH THE USE OR PERFORMANCE OF THIS SOFTWARE.
*/

/* jshint browser: true, esversion: 6, node:true */
/* globals console firebase */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Textbox from '../Textbox/Textbox';
import Checkbox from '../Checkbox/Checkbox';
import Label from '../Label/Label';
import Button from '../Button/Button';
import Datetime from '../Util/Datetime';
import Firebase from '../Util/Firebase';
import Service from '../Util/Service';
import $ from '../Util/Util';
import './Userview.css';

/**
 * Represents the user view.
 */

export default class Userview extends Component {

    /**
     * Properties.
     */

    static propTypes = {
        context:            PropTypes.object.isRequired,
        onLoading:          PropTypes.func.isRequired,
        onClose:            PropTypes.func.isRequired,
    }

    /**
     * Default state.
     */

    state = {
        edit:               false,
        user:               null
    }

    /**
     * Event that fires when the user wants to modify the selected user.
     */

    _modify = () => {

        // Setting the maintenance state

        this.setState({ edit: true }, () => {

            // Refreshing the screen

            this.refresh(this.state.user);
        });
    }

    /**
     * Event that fires when the user cancels the current maintenance.
     */

    _cancel = () => {

        // Closing the screen

        this.props.onClose();
    }

    /**
     * Saves the user.
     */

    _save = () => {

        // Validating name

        if (this.name.isEmpty()) {

            // Showing error

            $.fireEvent('snackbar', { message: this.props.context.locale.user.validation.missingname, error: true });

            return;
        }

        // Validating email

        if (this.email.isEmpty()) {

            // Showing error

            $.fireEvent('snackbar', { message: this.props.context.locale.user.validation.missingemail, error: true });

            return;
        }

        // Showing progress indicator

        this.props.onLoading(true);

        // Is it a user modification?

        if (this.state.user.uid) {

            // Saving the user

            Service.modifyUser(
                this.state.user.uid,
                this.name.getValue(),
                this.admin.getValue(),
                this.active.getValue()
            ).then(() => {

                // Hiding progress indicator

                this.props.onLoading(false);

                // Showing mesage

                $.fireEvent('snackbar', { message: this.props.context.locale.user.savedmessage });

                // Closing the view

                this.props.onClose(true);
            }).catch((error) => {

                // Hiding progress indicator

                this.props.onLoading(false);

                // Showing error

                $.fireEvent('snackbar', { message: this.props.context.locale.error.database, error: true });
            });
        } else {
            
            // Creates the user at Firebase level

            Firebase.createUser(
                this.email.getValue(),
                this.props.context.user.email,
                this.props.context.user.password,
                this.props.context.locale.locale
            ).then((uid) => {

                // Creates the user at database level

                Service.createUser(
                    uid,
                    this.email.getValue(),
                    this.name.getValue(),
                    this.admin.getValue()
                ).then(() => {

                    // Hiding progress indicator

                    this.props.onLoading(false);

                    // Showing error

                    $.fireEvent('snackbar', { message: this.props.context.locale.user.savedmessage });

                    // Closing the view

                    this.props.onClose(true);
                }).catch((error) => {

                    // Hiding progress indicator

                    this.props.onLoading(false);

                    // Showing error

                    $.fireEvent('snackbar', { message: this.props.context.locale.error.database, error: true });
                });
            }).catch((error) => {

                // Hiding progress indicator

                this.props.onLoading(false);

                // Showing error

                $.fireEvent('snackbar', { message: this.props.context.locale.error.database, error: true });
            });
        }
    }

    /**
     * Refreshes the screen.
     */

    refresh = (user) => {

        // Storing the user

        this.setState({ user: user, edit: user.uid === undefined ? true : this.state.edit }, () => {

            // Refreshing the user attributes
            
            if (this.name) {
                this.name.setValue(user.name);
                this.email.setValue(user.email);
                this.active.setValue(user.active);
                this.admin.setValue(user.admin);
            }
        });
    }

    /**
     * Toggles the status of the active flag.
     */

    _toggleActiveStatus = () => {

        // Toggles the status if the user is not the current user

        if (this.state.user.uid && this.state.user.uid !== this.props.context.user.uid) {
            this.active.setValue(!this.active.getValue());
        }
    }

    /**
     * Toggles the status of the admin flag.
     */

    _toggleAdminStatus = () => {

        // Toggles the status if the user is not the current user or if it is a new user

        if (!this.state.user.uid || (this.state.user.uid && this.state.user.uid !== this.props.context.user.uid)) {
            this.admin.setValue(!this.admin.getValue());
        }
    }

    /**
     * Sending password reset email to the user.
     */

    _passwordReset = () => {

        // Sending password reset email

        firebase.auth().sendPasswordResetEmail(this.state.user.email);

        // Showing mesage

        $.fireEvent('snackbar', { message: this.props.context.locale.user.passwordreset.confirm });
    }

    /** 
     * User view renderer.
     */

    render() {

        /**
         * Date formatter.
         * 
         * @param   {Date}      date    date that has to format
         * @return  {String}    date    formatted date
         */

        let formatDate = (date) => {
            return `${new Datetime(date).format('yyyy.mm.dd')} ${(date.getHours() < 10 ? '0' : '') + date.getHours()}:${(date.getMinutes() < 10 ? '0' : '') + date.getMinutes()}:${(date.getSeconds() < 10 ? '0' : '') + date.getSeconds()}`; 
        }; 

        return (
            <div className="userview" style={{ height: (this.props.context.environment.browser.info.mobile ? (this.props.context.environment.screen.usefullheight - 32) : (this.props.context.environment.screen.usefullheight - 64)) + 'px' }}>

                {/* Edit mode */}

                { this.state.edit && this.state.user && <div className="userview-edit vertical" style={{ height: (this.props.context.environment.screen.usefullheight - 64) + 'px' }}>

                    {/* Username */}

                    <Textbox ref={c => {this.name = c;}} label={this.props.context.locale.user.field.name.label} tooltip={this.props.context.locale.user.field.name.tooltip} comment={this.props.context.locale.user.field.name.comment} placeholder={this.props.context.locale.user.field.name.placeholder} labelposition="top" width={this.props.context.environment.browser.info.mobile ? (this.props.context.environment.screen.width - 64) : 320} isrequired />

                    {/* Email */}

                    <Textbox ref={c => {this.email = c;}} label={this.props.context.locale.user.field.email.label} tooltip={this.props.context.locale.user.field.email.tooltip} comment={this.props.context.locale.user.field.email.comment} placeholder={this.props.context.locale.user.field.email.placeholder} disabled={this.state.user.uid ? true : false} labelposition="top" width={this.props.context.environment.browser.info.mobile ? (this.props.context.environment.screen.width - 64) : 320} isrequired />

                    {/* Active */}

                    <div className="vertical">
                        <Label text={this.props.context.locale.user.field.active.label} />
                        <div className="horizontal center">
                            <Checkbox ref={c => {this.active = c;}} tooltip={this.props.context.locale.user.field.active.tooltip} disabled={this.state.user.uid ? (this.state.user.uid === this.props.context.user.uid) : true} />
                            <Label leftgap={8} text={this.props.context.locale.user.field.active.comment} onTouch={this._toggleActiveStatus} />
                        </div>
                    </div>

                    {/* Admin */}

                    <div className="vertical">
                        <Label text={this.props.context.locale.user.field.admin.label} topgap={16} />
                        <div className="horizontal center">
                            <Checkbox ref={c => {this.admin = c;}} tooltip={this.props.context.locale.user.field.admin.tooltip} disabled={(this.state.user.uid && this.state.user.uid === this.props.context.user.uid) ? true : false} />
                            <Label leftgap={8} text={this.props.context.locale.user.field.admin.comment} onTouch={this._toggleAdminStatus} />
                        </div>
                    </div>

                    {/* Password reset */}

                    {this.state.user.uid && <div className="userview-resetpassword" title={this.props.context.locale.user.passwordreset.tooltip} onClick={this._passwordReset}>{this.props.context.locale.user.passwordreset.label}</div> }
                </div> }

                {/* View mode */}

                { !this.state.edit && this.state.user && <div className="vertical">

                    {/* Username */}

                    <Label text={this.props.context.locale.user.field.name.label} />
                    <div className="userview-value">{ this.state.user.name }</div>

                    {/* Email */}

                    <Label text={this.props.context.locale.user.field.email.label} topgap={16} />
                    <a className="userview-value-email" href={`mailto:${this.state.user.email}`}>{this.state.user.email}</a>

                    {/* Active */}

                    <Label text={this.props.context.locale.user.field.active.label} topgap={16} />
                    <div className="userview-value">{ this.state.user.active ? this.props.context.locale.user.field.active.values.yes : this.props.context.locale.user.field.active.values.no }</div>

                    {/* Admin */}

                    <Label text={this.props.context.locale.user.field.admin.label} topgap={16} />
                    <div className="userview-value">{ this.state.user.admin ? this.props.context.locale.user.field.admin.values.yes : this.props.context.locale.user.field.admin.values.no }</div>

                    {/* Created */}

                    <Label text={this.props.context.locale.user.field.created.label} topgap={16} />
                    <div className="userview-value">{ formatDate(new Date(this.state.user.created)) }</div>

                    {/* Last login */}

                    <Label text={this.props.context.locale.user.field.lastlogin.label} topgap={16} />
                    <div className="userview-value">{ this.state.user.lastlogin ? formatDate(new Date(this.state.user.lastlogin)) : '-' }</div>
                </div> }

                {/* Buttons */}

                <div className="userview-buttons horizontal">

                    {/* Cancel button */}

                    { this.state.edit && <Button label={this.props.context.locale.user.buttons.cancel.label} tooltip={this.props.context.locale.user.buttons.cancel.tooltip} width={60} onTouch={this._cancel} /> }

                    {/* Spacer */}

                    <div className="flex"></div>

                    {/* Modify button */}

                    { !this.state.edit && this.state.user && <Button label={this.props.context.locale.user.buttons.modify.label} tooltip={this.props.context.locale.user.buttons.modify.tooltip} width={60} onTouch={this._modify} /> }

                    {/* Save button */}

                    { this.state.edit && <Button label={this.props.context.locale.user.buttons.save.label} tooltip={this.props.context.locale.user.buttons.save.tooltip} width={60} onTouch={this._save} /> }
                </div>
            </div>
        );
    }
}



// WEBPACK FOOTER //
// ./src/components/Userview/Userview.js